import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialogue, Case } from '../types';
import apiService from '../services/api';
import DialogueCard from '../components/Dialogues/DialogueCard';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  UserGroupIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const DialoguesPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [dialogues, setDialogues] = useState<Dialogue[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCase, setSelectedCase] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title'>('newest');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Load cases first
      const casesResponse = await apiService.getCases(1, 100); // Get up to 100 cases
      setCases(casesResponse.data);
      
      // Load dialogues for each case
      const allDialogues: Dialogue[] = [];
      for (const caseItem of casesResponse.data) {
        try {
          const dialogue = await apiService.getDialogueByCaseId(caseItem.caseId);
          if (dialogue) {
            allDialogues.push(dialogue);
          }
        } catch (err) {
          // Case might not have a dialogue yet, continue
          console.log(`No dialogue found for case ${caseItem.caseId}`);
        }
      }
      
      setDialogues(allDialogues);
    } catch (err) {
      console.error('Error loading dialogues:', err);
      setError('Failed to load dialogues. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDialogues = dialogues.filter(dialogue => {
    const matchesSearch = dialogue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dialogue.case?.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCase = selectedCase === 'all' || dialogue.caseId === selectedCase;
    
    return matchesSearch && matchesCase;
  });

  const sortedDialogues = [...filteredDialogues].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest':
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  const handleCreateNewDialogue = () => {
    navigate('/cases');
  };

  const handleDialogueClick = (dialogue: Dialogue) => {
    navigate(`/cases/${dialogue.case?.caseId || dialogue.caseId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dialogues...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600 mr-3" />
                Dialogues
              </h1>
              <p className="mt-2 text-gray-600">
                Manage and review your dialogue practice sessions
              </p>
            </div>
            <button
              onClick={handleCreateNewDialogue}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Dialogue
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search dialogues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'title')}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="title">Title A-Z</option>
              </select>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Case
                  </label>
                  <select
                    value={selectedCase}
                    onChange={(e) => setSelectedCase(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Cases</option>
                    {cases.map((caseItem) => (
                      <option key={caseItem.id} value={caseItem.id}>
                        {caseItem.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dialogues Grid */}
        {sortedDialogues.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No dialogues found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedCase !== 'all' 
                ? 'Try adjusting your search or filters.'
                : 'Get started by creating your first dialogue session.'
              }
            </p>
            {!searchTerm && selectedCase === 'all' && (
              <button
                onClick={handleCreateNewDialogue}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create First Dialogue
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedDialogues.map((dialogue) => (
              <DialogueCard
                key={dialogue.id}
                dialogue={dialogue}
                onClick={() => handleDialogueClick(dialogue)}
              />
            ))}
          </div>
        )}

        {/* Stats */}
        {dialogues.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dialogue Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{dialogues.length}</div>
                <div className="text-sm text-gray-600">Total Dialogues</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {dialogues.reduce((sum, d) => sum + (d.turns?.length || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Total Turns</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {dialogues.reduce((sum, d) => sum + (d.audioFiles?.length || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Audio Files</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {dialogues.reduce((sum, d) => sum + (d.ratings?.length || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Ratings</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DialoguesPage;
